
import { Button } from "@/components/ui/button";
import { MinusCircle, ChevronDown, ChevronUp, Building, Users } from 'lucide-react';
import { MapLocation } from '@/types/location';
import { useState } from 'react';
import { Badge } from "@/components/ui/badge";

interface SelectedLocationsListProps {
  selectedLocations: Set<number>;
  locations: MapLocation[];
  onDeselect: (id: number) => void;
  attentionScreenCounts: Map<number, number>;
  isRemoving?: boolean;
}

export const SelectedLocationsList = ({ 
  selectedLocations, 
  locations, 
  onDeselect, 
  attentionScreenCounts,
  isRemoving = false
}: SelectedLocationsListProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const MAX_VISIBLE_ITEMS = 3;

  if (selectedLocations.size === 0) return null;

  const selectedLocationsList = Array.from(selectedLocations)
    .map(id => locations.find(l => l.id === id))
    .filter((location): location is MapLocation => location !== undefined);

  const visibleLocations = isExpanded 
    ? selectedLocationsList 
    : selectedLocationsList.slice(0, MAX_VISIBLE_ITEMS);

  const hasMore = selectedLocationsList.length > MAX_VISIBLE_ITEMS;

  return (
    <div className="bg-card dark:bg-card p-4 rounded-lg shadow-sm border border-border">
      <div className="flex items-center gap-2 mb-3">
        <Building className="h-4 w-4 text-primary" />
        <h4 className="font-semibold text-foreground">Selected Locations</h4>
        <Badge variant="secondary" className="ml-auto">
          {selectedLocations.size}
        </Badge>
      </div>
      
      <div className="space-y-2">
        {visibleLocations.map(location => {
          const isAttentionLocation = location.mediaOwner === 'Attention';
          const selectedScreens = isAttentionLocation 
            ? (attentionScreenCounts.get(location.id) || 0)
            : location.devices;

          return (
            <div 
              key={location.id} 
              className="flex items-center justify-between bg-muted/50 dark:bg-muted/20 p-3 rounded-lg hover:bg-muted dark:hover:bg-muted/40 transition-colors"
            >
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <p className="font-medium text-foreground text-sm">{location.name}</p>
                  {isAttentionLocation && (
                    <Badge variant="outline" className="text-xs">
                      Partial
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-3 text-xs text-gray-600">
                  <div className="flex items-center gap-1">
                    <Users className="h-3 w-3" />
                    <span>{selectedScreens} screens</span>
                  </div>
                  <span className="text-gray-400">•</span>
                  <span>{location.mediaOwner}</span>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                disabled={isRemoving}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  console.log(`🗑️ REMOVE BUTTON CLICKED: Location ${location.name} (ID: ${location.id})`);
                  onDeselect(location.id);
                }}
                className="text-red-500 hover:text-red-700 hover:bg-red-50 ml-2 disabled:opacity-50"
              >
                <MinusCircle className="h-4 w-4" />
              </Button>
            </div>
          );
        })}

        {hasMore && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            className="w-full mt-2 text-gray-600 hover:text-gray-900 flex items-center justify-center gap-2"
          >
            {isExpanded ? (
              <>
                Show Less <ChevronUp className="h-4 w-4" />
              </>
            ) : (
              <>
                Show {selectedLocationsList.length - MAX_VISIBLE_ITEMS} More <ChevronDown className="h-4 w-4" />
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  );
};
